import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Button, Table, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { useAdminMunicipality, useAdminMunicipalityUsers } from 'api/hooks/use-admin-municipalities';
import MunicipalitySettingsForm from './MunicipalitySettingsForm';
import MunicipalityAnalyticConfigs from './MunicipalityAnalyticConfigs';
import MunicipalityPipeline from './MunicipalityPipeline';
import MunicipalityAwsProvisioningPanel from './MunicipalityAwsProvisioningPanel';
import MunicipalityBucketBrowser from './MunicipalityBucketBrowser';
import MunicipalitySqlSchema from './MunicipalitySqlSchema';
import DatumAdminTab from './DatumAdminTab';
import AdminTabs from 'admin/shared/AdminTabs'
import UserStatusBadge from 'shared/badges/user-status-badge'

const DEPLOYMENT_TYPE_LABELS = {
  cloud: 'Cloud',
  on_prem: 'On-Premises'
}

const sqlServerVersionLabel = (year) => (year ? `SQL Server ${year}` : 'Unknown / Not set')

const itContactName = (municipality) =>
  [municipality.it_contact_first_name, municipality.it_contact_last_name].filter(Boolean).join(' ')

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const refreshScheduleLabel = (municipality) => {
  switch (municipality.refresh_schedule) {
    case 'nightly':
      return 'Nightly'
    case 'weekly':
      return `Weekly (${DAY_NAMES[municipality.refresh_day_of_week] ?? 'Unknown day'})`
    case 'monthly':
      return `Monthly (day ${municipality.refresh_day_of_month})`
    default:
      return 'None'
  }
}

const MunicipalityDetail = () => {
  const { id } = useParams()
  const [activeTab, setActiveTab] = useState('info')
  const { data: municipality, isLoading: municipalityLoading, error: municipalityError } = useAdminMunicipality({ id })
  const { data: users, isLoading: usersLoading, error: usersError } = useAdminMunicipalityUsers({ municipalityId: id })

  if (municipalityLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  if (municipalityError) {
    return (
      <Alert variant="danger">
        Error loading municipality: {municipalityError.message}
      </Alert>
    )
  }

  return (
    <div>
      <div className="bg-white border-bottom mb-4 py-3" style={{ margin: '-1rem -1rem 1rem -1rem', padding: '1rem' }}>
        <div className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">{municipality.name}</h3>
          <div>
            <Button as={Link} to={`/admin/municipalities/${id}/custom-analytics`} variant="light" className="text-primary me-2" size="sm">
              Custom Analytics
            </Button>
            <Button as={Link} to={`/admin/municipalities/${id}/edit`} variant="primary" size="sm" className="me-2">
              Edit Municipality
            </Button>
            <Button as={Link} to="/admin/municipalities" variant="light" className="text-primary" size="sm">
              Back to List
            </Button>
          </div>
        </div>
      </div>

      <AdminTabs
        tabs={[
          { name: 'Information', value: 'info' },
          { name: `Pipeline (${municipality.municipality_sections?.length || municipality.sections?.length || 0})`, value: 'pipeline' },
          { name: `Users (${users?.length || 0})`, value: 'users' },
          { name: 'Settings', value: 'settings' },
          { name: 'Files', value: 'files' },
          { name: 'SQL Schema', value: 'sql_schema' },
          { name: 'Reports (Datum)', value: 'datum' }
        ]}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {activeTab === 'info' && (
        <Row>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header><h5>Basic Information</h5></Card.Header>
              <Card.Body>
                <dl className="row mb-0">
                  <dt className="col-sm-5">Name:</dt>
                  <dd className="col-sm-7">{municipality.name}</dd>
                  <dt className="col-sm-5">Abbreviation:</dt>
                  <dd className="col-sm-7">{municipality.abbreviation || 'N/A'}</dd>
                  <dt className="col-sm-5">Directory:</dt>
                  <dd className="col-sm-7">{municipality.directory || 'N/A'}</dd>
                  <dt className="col-sm-5">Email Domain:</dt>
                  <dd className="col-sm-7">{municipality.email_domain || 'N/A'}</dd>
                  <dt className="col-sm-5">IT Contact Name:</dt>
                  <dd className="col-sm-7">{itContactName(municipality) || 'N/A'}</dd>
                  <dt className="col-sm-5">IT Contact:</dt>
                  <dd className="col-sm-7">{municipality.it_contact_email || 'N/A'}</dd>
                  <dt className="col-sm-5">Department Segment:</dt>
                  <dd className="col-sm-7">{municipality.setting?.department_segment || 'N/A'}</dd>
                </dl>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header><h5>Configuration</h5></Card.Header>
              <Card.Body>
                <dl className="row mb-0">
                  <dt className="col-sm-5">Deployment:</dt>
                  <dd className="col-sm-7">{DEPLOYMENT_TYPE_LABELS[municipality.deployment_type] || 'Unknown / Not set'}</dd>
                  <dt className="col-sm-5">SQL Server Version:</dt>
                  <dd className="col-sm-7">{sqlServerVersionLabel(municipality.sql_server_version)}</dd>
                  <dt className="col-sm-5">Bucket Name:</dt>
                  <dd className="col-sm-7">{municipality.bucket_name || 'N/A'}</dd>
                  <dt className="col-sm-5">Output Bucket:</dt>
                  <dd className="col-sm-7">{municipality.output_bucket || 'N/A'}</dd>
                  <dt className="col-sm-5">Has Allocations:</dt>
                  <dd className="col-sm-7">{municipality.has_allocations ? 'Yes' : 'No'}</dd>
                  <dt className="col-sm-5">Custom PCard:</dt>
                  <dd className="col-sm-7">{municipality.is_custom_pcard ? 'Yes' : 'No'}</dd>
                  <dt className="col-sm-5">Refresh Schedule:</dt>
                  <dd className="col-sm-7">{refreshScheduleLabel(municipality)}</dd>
                </dl>
              </Card.Body>
            </Card>
            <MunicipalityAwsProvisioningPanel municipality={municipality} />
          </Col>
        </Row>
      )}

      {activeTab === 'pipeline' && (
        <MunicipalityPipeline municipality={municipality} />
      )}

      {activeTab === 'users' && (
        <Card>
          <Card.Body>
            {usersLoading ? (
              <div className="text-center p-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : usersError ? (
              <Alert variant="danger">Error loading users: {usersError.message}</Alert>
            ) : users && users.length > 0 ? (
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Admin</th>
                    <th>Analytics App</th>
                    <th>Access Management App</th>
                    <th>Beta</th>
                    <th>Status</th>
                    <th>Last Login</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td><Link to={`/admin/users/${user.id}`}>{user.first_name} {user.last_name}</Link></td>
                      <td>{user.email}</td>
                      <td>{user.role}</td>
                      <td>{user.is_admin ? 'Yes' : 'No'}</td>
                      <td>{user.has_analytics_app ? 'Yes' : 'No'}</td>
                      <td>{user.has_sod_app ? 'Yes' : 'No'}</td>
                      <td>{user.is_beta ? 'Yes' : 'No'}</td>
                      <td>
                        <UserStatusBadge state={user.blocked ? 'blocked' : 'active'} />
                        {!user.blocked && user.block_scheduled_for && (
                          <div className="small text-muted mt-1">
                            Auto-block {new Date(`${user.block_scheduled_for}T00:00:00`).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td>
                        {user.last_login_at
                          ? new Date(user.last_login_at).toLocaleDateString()
                          : <span className="text-muted">Never</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info" className="mb-0">No users found for this municipality.</Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {activeTab === 'settings' && (
        <MunicipalitySettingsForm municipalityId={id}>
          <MunicipalityAnalyticConfigs municipalityId={id} />
        </MunicipalitySettingsForm>
      )}

      {activeTab === 'files' && (
        <MunicipalityBucketBrowser municipality={municipality} />
      )}

      {activeTab === 'sql_schema' && (
        <MunicipalitySqlSchema municipality={municipality} />
      )}

      {activeTab === 'datum' && (
        <DatumAdminTab municipalityId={id} municipality={municipality} />
      )}
    </div>
  )
}

export default MunicipalityDetail
